import { useQuery } from "@tanstack/react-query";

import { supabase } from "../supabaseClient";
import { PAGE_SIZE } from "../components/Utils/constant";

const getAirlineConditionsList = async () => {
  const { data: companyList, error: companyError } = await supabase
    .from("Companies")
    .select("*");
  const { data: _locationList, error: locationError } = await supabase
    .from("Locations")
    .select("*");

  const { data: Drivers, error: driverErr } = await supabase
    .from("Drivers")
    .select(
      `
            DriverID,
            FirstName,
            LastName,
            Email,
            IsActive,
            auth_id,
            driver_auth!Drivers_auth_id_fkey (
              id,
              email,
              is_active,
              driver_profiles (
                id,
                first_name,
                last_name,
                phone,
                license_number,
                employment_status
              )
            )
          `
    )
    .not("auth_id", "is", null);
  const { data: vehicleList, error: vehicleError } = await supabase
    .from("Vehicles")
    .select("*");
  const { data: airlineList, error: airlineError } = await supabase
    .from("Airlines")
    .select("*");
  const {
    data: _scheduleTransportationTypeList,
    error: scheduleTransportationTypeError,
  } = await supabase.from("ScheduleTransportationTypes").select("*");

  const locationList = locationError ? [] : _locationList;
  const uniqueLocations = locationList.reduce((acc, current) => {
    if (!acc.find((item) => item.Code === current.Code)) {
      acc.push(current);
    }
    return acc;
  }, []);

  // Transform driver data to match expected format
  const transformedDriverList =
    Drivers?.map((driver) => ({
      DriverID: driver.DriverID,
      FirstName:
        driver.FirstName ||
        driver.driver_auth?.driver_profiles?.[0]?.first_name ||
        "",
      LastName:
        driver.LastName ||
        driver.driver_auth?.driver_profiles?.[0]?.last_name ||
        "",
      Email: driver.Email || driver.driver_auth?.email || "",
      Phone: driver.driver_auth?.driver_profiles?.[0]?.phone || "",
      LicenseNumber:
        driver.driver_auth?.driver_profiles?.[0]?.license_number || "",
      IsActive: driver.driver_auth?.is_active ?? false,
      EmploymentStatus: driver.driver_profiles?.[0]?.employment_status || "",
    })) || [];

  return {
    airlineList: airlineError
      ? []
      : airlineList.sort((a, b) => a.Description?.localeCompare(b.Description)),
    companyList: companyError
      ? []
      : companyList.sort((a, b) => a.Description?.localeCompare(b.Description)),
    locationList: locationError
      ? []
      : uniqueLocations.sort((a, b) =>
          a.Description?.localeCompare(b.Description)
        ),
    driverList: driverErr
      ? []
      : transformedDriverList.sort((a, b) =>
          `${a.FirstName} ${a.LastName}`.localeCompare(
            `${b.FirstName} ${b.LastName}`
          )
        ),
    vehicleList: vehicleError
      ? []
      : vehicleList.sort((a, b) => a.Model?.localeCompare(b.Model)),
    scheduleTransportationTypeList: scheduleTransportationTypeError
      ? []
      : _scheduleTransportationTypeList,
  };
};

const getTripConditionsList = async () => {
  const { data: companyList, error: companyError } = await supabase
    .from("Companies")
    .select("*");
  const { data: _locationList, error: locationError } = await supabase
    .from("Locations")
    .select("*");

  // Updated to use driver_auth table instead of Drivers table
  // This ensures consistency with the new driver authentication system
  const { data: Drivers, error: driverErr } = await supabase
    .from("Drivers")
    .select(
      `
            DriverID,
            FirstName,
            LastName,
            Email,
            IsActive,
            auth_id,
            driver_auth!Drivers_auth_id_fkey (
              id,
              email,
              is_active,
              driver_profiles (
                id,
                first_name,
                last_name,
                phone,
                license_number,
                employment_status
              )
            )
          `
    )
    .not("auth_id", "is", null);

  const { data: vehicleList, error: vehicleError } = await supabase
    .from("Vehicles")
    .select("*");
  const { data: airlineList, error: airlineError } = await supabase
    .from("Airlines")
    .select("*");
  const {
    data: _scheduleTransportationTypeList,
    error: scheduleTransportationTypeError,
  } = await supabase.from("ScheduleTransportationTypes").select("*");

  // Fetch managers for trip assignment — source from Managers table (where names
  // live), join to Users for primary LocationID, and keep only active managers
  // with an assigned airport. Avoids the 105-row noise of unassigned RoleID=2 users.
  const { data: mgrRows, error: managerError } = await supabase
    .from("Managers")
    .select(
      `
      UserID,
      firstname,
      lastname,
      email,
      isactive,
      Users:UserID (LocationID)
    `
    )
    .eq("isactive", true);

  const locationList = locationError ? [] : _locationList;
  const uniqueLocations = locationList.reduce((acc, current) => {
    if (!acc.find((item) => item.Code === current.Code)) {
      acc.push(current);
    }
    return acc;
  }, []);

  // Transform driver data to match expected format from driver_auth table
  // This ensures compatibility with existing components that expect the old Drivers table format
  const transformedDriverList =
    Drivers?.map((driver) => ({
      DriverID: driver.DriverID,
      FirstName:
        driver.FirstName ||
        driver.driver_auth?.driver_profiles?.[0]?.first_name ||
        "",
      LastName:
        driver.LastName ||
        driver.driver_auth?.driver_profiles?.[0]?.last_name ||
        "",
      Email: driver.Email || driver.driver_auth?.email || "",
      Phone: driver.driver_auth?.driver_profiles?.[0]?.phone || "",
      LicenseNumber:
        driver.driver_auth?.driver_profiles?.[0]?.license_number || "",
      IsActive: driver.driver_auth?.is_active ?? false,
      EmploymentStatus: driver.driver_profiles?.[0]?.employment_status || "",
    })) || [];

  const tripLocationCodeById = (locationError ? [] : _locationList).reduce(
    (acc, loc) => {
      acc[loc.LocationID] = loc.Code;
      return acc;
    },
    {}
  );

  const managerList = managerError
    ? []
    : (mgrRows || [])
        .map((m) => {
          const userRow = Array.isArray(m.Users) ? m.Users[0] : m.Users;
          const locationId = userRow?.LocationID || null;
          const code = locationId ? tripLocationCodeById[locationId] : null;
          return {
            UserID: m.UserID,
            Email: m.email,
            FirstName: m.firstname || "",
            LastName: m.lastname || "",
            LocationID: locationId,
            AirportCodes: code ? [code] : [],
          };
        })
        .filter((m) => m.AirportCodes.length > 0)
        .sort((a, b) =>
          `${a.FirstName} ${a.LastName}`
            .trim()
            .localeCompare(`${b.FirstName} ${b.LastName}`.trim())
        );

  return {
    airlineList: airlineError
      ? []
      : airlineList.sort((a, b) => a.Description?.localeCompare(b.Description)),
    companyList: companyError
      ? []
      : companyList.sort((a, b) => a.Description?.localeCompare(b.Description)),
    locationList: locationError
      ? []
      : uniqueLocations.sort((a, b) =>
          a.Description?.localeCompare(b.Description)
        ),
    driverList: driverErr
      ? []
      : transformedDriverList.sort((a, b) =>
          `${a.FirstName} ${a.LastName}`.localeCompare(
            `${b.FirstName} ${b.LastName}`
          )
        ),
    vehicleList: vehicleError
      ? []
      : vehicleList.sort((a, b) => a.Model?.localeCompare(b.Model)),
    scheduleTransportationTypeList: scheduleTransportationTypeError
      ? []
      : _scheduleTransportationTypeList,
    managerList,
  };
};

const airportList = [
  "LAX",
  "BUR",
  "SDF",
  "LAS",
  "MCO",
  "MSP",
  "CVG",
  "PHL",
  "DAY",
];

const getManagersAndLocations = async () => {
  const { data: managerList, error: managerError } = await supabase
    .from("Users")
    .select("*")
    .eq("RoleID", 2);
  const { data: _locationList, error: locationError } = await supabase
    .from("Locations")
    .select("*")
    .in("Code", airportList);
  const locationList = locationError ? [] : _locationList;
  const uniqueCodes = locationList.reduce((acc, current) => {
    if (!acc.find((item) => item.Code === current.Code)) {
      acc.push(current);
    }
    return acc;
  }, []);

  return {
    managerList: managerError
      ? []
      : managerList.sort((a, b) => a.Email?.localeCompare(b.Email)),
    locationList: uniqueCodes.sort((a, b) =>
      a.Description?.localeCompare(b.Description)
    ),
  };
};

const getManagers = async () => {
  const { data: managerList } = await supabase
    .from("Users")
    .select(
      `
      UserID,
      Email,
      Managers:Managers (firstname, lastname),
      UserLocations (
        LocationID,
        Locations:LocationID (
          LocationID,
          Description,
          Code
        )
      )
    `
    )
    .eq("RoleID", 2);

  return (
    managerList?.map((_item) => ({
      UserID: _item.UserID,
      Email: _item.Email,
      Managers: Array.isArray(_item.Managers) ? _item.Managers[0] : _item.Managers,
      Locations: _item.UserLocations?.map((ul) => ul.Locations).filter(Boolean) || [],
    })) || []
  );
};

const getManagerDetail = async (managerID) => {
  const { data: driverData, error: driverErr } = await supabase
    .from("Users")
    .select(
      `
      Email,
      Managers:Managers (firstname, lastname),
      LocationID,
      UserLocations (
        LocationID
      )
    `
    )
    .eq("UserID", managerID);

  if (driverErr || !driverData?.[0]) return null;
  const manager = driverData[0];
  return {
    ...manager,
    LocationIDs: manager.UserLocations?.map((ul) => ul.LocationID) || [],
  };
};

const getScheduleDetail = async (scheduleID) => {
  const { data: scheduleData, error: scheduleErr } = await supabase
    .from("Schedule")
    .select(
      `
        ScheduleID,
        ScheduleDate,
        CompanyID,
        RateOverride,
        FlightNo,
        AirlineID,
        ScheduleStatusID,
        TransportationTypeID,
        DriverID,
        VehicleID,
        Pickups:SchedulePickups (
          Passengers, 
          Location:LocationID (LocationID, Description)
        ),
        Dropoffs:ScheduleDropoffs (
          Location:LocationID (LocationID, Description)
        )
      `
    )
    .eq("ScheduleID", scheduleID);

  return scheduleErr ? null : scheduleData[0];
};

const getDriverDetail = async (driverID) => {
  const { data: driverData, error: driverErr } = await supabase
    .from("Drivers")
    .select(
      `
        DriverID,
        FirstName,
        LastName,
        Email,
        IsActive,
        auth_id,
        driver_auth!Drivers_auth_id_fkey (
          id,
          email,
          is_active,
          driver_profiles (
            id,
            first_name,
            last_name,
            phone,
            license_number,
            license_expiry,
            employment_status,
            hire_date,
            avatar_url
          )
        )
      `
    )
    .eq("DriverID", driverID)
    .eq("IsActive", true);

  if (driverErr || !driverData?.[0]) return null;

  const driver = driverData[0];
  const profile = driver.driver_auth?.driver_profiles?.[0];

  // Transform to match expected format
  return {
    DriverID: driver.DriverID,
    FirstName: profile?.first_name ?? "",
    LastName: profile?.last_name || "",
    Email: driver.Email,
    Phone: profile?.phone || "",
    LicenseNumber: profile?.license_number || "",
    LicenseExpiry: profile?.license_expiry || "",
    EmploymentStatus: profile?.employment_status || "",
    HireDate: profile?.hire_date || "",
    AvatarUrl: profile?.avatar_url || "",
    IsActive: driver.is_active,
    EmailVerified: driver.driver_auth?.email_verified,
    CreatedAt: driver.created_at,
    UpdatedAt: driver.updated_at,
  };
};

const getLocations = async () => {
  const { data: _locationList, error: locationError } = await supabase
    .from("Locations")
    .select("*");
  const locationList = locationError ? [] : _locationList;
  const uniqueCodes = locationList.reduce((acc, current) => {
    if (!acc.find((item) => item.Code === current.Code)) {
      acc.push(current);
    }
    return acc;
  }, []);

  return uniqueCodes.sort((a, b) =>
    a.Description?.localeCompare(b.Description)
  );
};

const getReportConditions = async () => {
  const { data: Drivers, error: driverError } = await supabase
    .from("Drivers")
    .select(
      `
            DriverID,
            FirstName,
            LastName,
            Email,
            IsActive,
            auth_id,
            driver_auth!Drivers_auth_id_fkey (
              id,
              email,
              is_active,
              driver_profiles (
                id,
                first_name,
                last_name,
                phone,
                license_number,
                employment_status
              )
            )
          `
    )
    .not("auth_id", "is", null);
  const { data: _locationList, error: locationError } = await supabase
    .from("Locations")
    .select("*");
  const locationList = locationError ? [] : _locationList;
  const uniqueLocations = locationList.reduce((acc, current) => {
    if (!acc.find((item) => item.Code === current.Code)) {
      acc.push(current);
    }
    return acc;
  }, []);
  const { data: vehicleList, error: vehicleError } = await supabase
    .from("Vehicles")
    .select("*");
  const { data: companyList, error: companyError } = await supabase
    .from("Companies")
    .select("*");
  const { data: airlineList, error: airlineError } = await supabase
    .from("Airlines")
    .select("*");

  // Transform driver data to match expected format
  const transformedDriverList =
    Drivers?.map((driver) => ({
      DriverID: driver.DriverID,
      FirstName:
        driver.FirstName ||
        driver.driver_auth?.driver_profiles?.[0]?.first_name ||
        "",
      LastName:
        driver.LastName ||
        driver.driver_auth?.driver_profiles?.[0]?.last_name ||
        "",
      Email: driver.Email || driver.driver_auth?.email || "",
      Phone: driver.driver_auth?.driver_profiles?.[0]?.phone || "",
      LicenseNumber:
        driver.driver_auth?.driver_profiles?.[0]?.license_number || "",
      IsActive: driver.driver_auth?.is_active ?? false,
      EmploymentStatus: driver.driver_profiles?.[0]?.employment_status || "",
    })) || [];

  // Manager dropdown: source directly from Managers table (where names live),
  // pull each manager's primary airport via the linked Users.LocationID, and
  // include only active managers who actually have an airport assigned.
  const { data: mgrRows, error: managerError } = await supabase
    .from("Managers")
    .select(
      `
      UserID,
      firstname,
      lastname,
      email,
      isactive,
      Users:UserID (LocationID)
    `
    )
    .eq("isactive", true);

  const locationCodeById = locationList.reduce((acc, loc) => {
    acc[loc.LocationID] = loc.Code;
    return acc;
  }, {});

  const managerList = managerError
    ? []
    : (mgrRows || [])
        .map((m) => {
          const userRow = Array.isArray(m.Users) ? m.Users[0] : m.Users;
          const locationId = userRow?.LocationID || null;
          const code = locationId ? locationCodeById[locationId] : null;
          return {
            UserID: m.UserID,
            Email: m.email,
            FirstName: m.firstname || "",
            LastName: m.lastname || "",
            LocationID: locationId,
            AirportCodes: code ? [code] : [],
          };
        })
        .filter((m) => m.AirportCodes.length > 0)
        .sort((a, b) =>
          `${a.FirstName} ${a.LastName}`
            .trim()
            .localeCompare(`${b.FirstName} ${b.LastName}`.trim())
        );

  return {
    airlineList: airlineError
      ? []
      : airlineList.sort((a, b) => a.Description?.localeCompare(b.Description)),
    vehicleList: vehicleError
      ? []
      : vehicleList.sort((a, b) => a.Model?.localeCompare(b.Model)),
    companyList: companyError
      ? []
      : companyList.sort((a, b) => a.Description?.localeCompare(b.Description)),
    locationList: uniqueLocations.sort((a, b) =>
      a.Description?.localeCompare(b.Description)
    ),
    driverList: driverError
      ? []
      : transformedDriverList.sort((a, b) =>
          `${a.FirstName} ${a.LastName}`.localeCompare(
            `${b.FirstName} ${b.LastName}`
          )
        ),
    managerList,
  };
};

const getSearchConditions = async () => {
  const { data: Drivers, error: driverError } = await supabase
    .from("Drivers")
    .select(
      `
            DriverID,
            FirstName,
            LastName,
            Email,
            IsActive,
            auth_id,
            driver_auth!Drivers_auth_id_fkey (
              id,
              email,
              is_active,
              driver_profiles (
                id,
                first_name,
                last_name,
                phone,
                license_number,
                employment_status
              )
            )
          `
    )
    .not("auth_id", "is", null);
  const { data: _locationList, error: locationError } = await supabase
    .from("Locations")
    .select("*")
    .eq("LocationTypeID", 2);
  const locationList = locationError ? [] : _locationList;
  const uniqueLocations = locationList.reduce((acc, current) => {
    if (!acc.find((item) => item.Code === current.Code)) {
      acc.push(current);
    }
    return acc;
  }, []);
  const { data: vehicleList, error: vehicleError } = await supabase
    .from("Vehicles")
    .select("*");
  const { data: companyList, error: companyError } = await supabase
    .from("Companies")
    .select("*");
  const { data: airlineList, error: airlineError } = await supabase
    .from("Airlines")
    .select("*");

  // Transform driver data to match expected format
  const transformedDriverList =
    Drivers?.map((driver) => ({
      DriverID: driver.DriverID,
      FirstName:
        driver.FirstName ||
        driver.driver_auth?.driver_profiles?.[0]?.first_name ||
        "",
      LastName:
        driver.LastName ||
        driver.driver_auth?.driver_profiles?.[0]?.last_name ||
        "",
      Email: driver.Email || driver.driver_auth?.email || "",
      Phone: driver.driver_auth?.driver_profiles?.[0]?.phone || "",
      LicenseNumber:
        driver.driver_auth?.driver_profiles?.[0]?.license_number || "",
      IsActive: driver.driver_auth?.is_active ?? false,
      EmploymentStatus: driver.driver_profiles?.[0]?.employment_status || "",
    })) || [];

  // Manager dropdown: use the same source as the Reports page — all active
  // managers who have an airport code assigned via their linked User.
  // Restricting to managers who already have Schedule rows hid brand-new
  // managers before their first trip landed, which made the filter feel
  // broken (only 2 of 7 managers visible). See getReportConditions above.
  const { data: mgrRows, error: managerError } = await supabase
    .from("Managers")
    .select(
      `
      UserID,
      firstname,
      lastname,
      email,
      isactive,
      Users:UserID (LocationID)
    `
    )
    .eq("isactive", true);

  const _locationCodeById = locationList.reduce((acc, loc) => {
    acc[loc.LocationID] = loc.Code;
    return acc;
  }, {});

  const managerList = managerError
    ? []
    : (mgrRows || [])
        .map((m) => {
          const userRow = Array.isArray(m.Users) ? m.Users[0] : m.Users;
          const locationId = userRow?.LocationID || null;
          const code = locationId ? _locationCodeById[locationId] : null;
          return {
            UserID: m.UserID,
            Email: m.email,
            FirstName: m.firstname || "",
            LastName: m.lastname || "",
            LocationID: locationId,
            AirportCodes: code ? [code] : [],
          };
        })
        .filter((m) => m.AirportCodes.length > 0)
        .sort((a, b) =>
          `${a.FirstName} ${a.LastName}`
            .trim()
            .localeCompare(`${b.FirstName} ${b.LastName}`.trim())
        );

  return {
    airlineList: airlineError
      ? []
      : airlineList.sort((a, b) => a.Description?.localeCompare(b.Description)),
    vehicleList: vehicleError
      ? []
      : vehicleList.sort((a, b) => a.Model?.localeCompare(b.Model)),
    companyList: companyError
      ? []
      : companyList.sort((a, b) => a.Description?.localeCompare(b.Description)),
    locationList: uniqueLocations.sort((a, b) =>
      a.Description?.localeCompare(b.Description)
    ),
    driverList: driverError
      ? []
      : transformedDriverList.sort((a, b) =>
          `${a.FirstName} ${a.LastName}`.localeCompare(
            `${b.FirstName} ${b.LastName}`
          )
        ),
    managerList,
  };
};

const getDriverNotes = async (search, page = 1, pageSize = PAGE_SIZE) => {
  let query = supabase
    .from("Driver_Notes")
    .select(
      `
      ID,
      created_at,
      Memo,
      Driver:DriverID (
        DriverID,
        DriverCode
      )
    `,
      { count: "exact" }
    ) // ✅ Enable counting
    .order("created_at", { ascending: false }) // Optional: sort newest first
    .range((page - 1) * pageSize, page * pageSize - 1); // ✅ Pagination

  if (
    search &&
    typeof search.type === "string" &&
    typeof search.which === "string" &&
    typeof search.text === "string" &&
    search.text.trim() !== ""
  ) {
    const field = search.type;
    const value = search.text.trim();

    if (field === "DriverCode") {
      let driverFilter;

      switch (search.which.toLowerCase()) {
        case "equals":
          driverFilter = supabase
            .from("driver_auth")
            .select("id")
            .eq("email", value);
          break;
        case "contains":
          driverFilter = supabase
            .from("driver_auth")
            .select("id")
            .ilike("email", `%${value}%`);
          break;
        case "starts with":
          driverFilter = supabase
            .from("driver_auth")
            .select("id")
            .ilike("email", `${value}%`);
          break;
        case "ends with":
          driverFilter = supabase
            .from("driver_auth")
            .select("id")
            .ilike("email", `%${value}`);
          break;
        default:
          break;
      }

      const { data: drivers, error: driverError } = await driverFilter;
      if (driverError || !drivers?.length) return { data: [], count: 0 };

      const driverIDs = drivers.map((d) => d.id);
      query = query.in("DriverID", driverIDs);
    } else if (field === "ID") {
      query = query.eq("ID", Number(value));
    } else {
      switch (search.which.toLowerCase()) {
        case "equals":
          query = query.eq(field, value);
          break;
        case "contains":
          query = query.ilike(field, `%${value}%`);
          break;
        case "starts with":
          query = query.ilike(field, `${value}%`);
          break;
        case "ends with":
          query = query.ilike(field, `%${value}`);
          break;
        default:
          break;
      }
    }
  }

  const { data, count, error } = await query;
  return error ? { data: [], count: 0 } : { data, count };
};

const getHQCompanies = async (search, page = 1, pageSize = PAGE_SIZE) => {
  let query = supabase
    .from("HQCompanies")
    .select(
      `
      HQCompanyID,
      created_at,
      HQ:HQID (
        HQID,
        Code
      ),
      Company:CompanyID (
        CompanyID,
        Name
      )
    `,
      { count: "exact" }
    ) // ✅ Enable counting
    .order("created_at", { ascending: false }) // Optional: sort newest first
    .range((page - 1) * pageSize, page * pageSize - 1); // ✅ Pagination

  if (
    search &&
    typeof search.type === "string" &&
    typeof search.which === "string" &&
    typeof search.text === "string" &&
    search.text.trim() !== ""
  ) {
    const field = search.type;
    const value = search.text.trim();

    if (field === "Code") {
      let HQFilter;

      switch (search.which.toLowerCase()) {
        case "equals":
          HQFilter = supabase.from("HQs").select("HQID").eq("Code", value);
          break;
        case "contains":
          HQFilter = supabase
            .from("HQs")
            .select("HQID")
            .ilike("Code", `%${value}%`);
          break;
        case "starts with":
          HQFilter = supabase
            .from("HQs")
            .select("HQID")
            .ilike("Code", `${value}%`);
          break;
        case "ends with":
          HQFilter = supabase
            .from("HQs")
            .select("HQID")
            .ilike("Code", `%${value}`);
          break;
        default:
          break;
      }

      const { data: HQs, error: HQError } = await HQFilter;
      if (HQError || !HQs?.length) return { data: [], count: 0 };

      const HQIDs = HQs.map((h) => h.HQID);
      query = query.in("HQID", HQIDs);
    } else if (field === "Name") {
      let CompanyFilter;

      switch (search.which.toLowerCase()) {
        case "equals":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .eq("Name", value);
          break;
        case "contains":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .ilike("Name", `%${value}%`);
          break;
        case "starts with":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .ilike("Name", `${value}%`);
          break;
        case "ends with":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .ilike("Name", `%${value}`);
          break;
        default:
          break;
      }

      const { data: Companies, error: CompanyError } = await CompanyFilter;
      if (CompanyError || !Companies?.length) return { data: [], count: 0 };

      const CompanyIDs = Companies.map((h) => h.CompanyID);
      query = query.in("CompanyID", CompanyIDs);
    } else if (field === "HQCompanyID") {
      query = query.eq("HQCompanyID", Number(value));
    }
  }

  const { data, count, error } = await query;
  return error ? { data: [], count: 0 } : { data, count };
};

const getHQs = async (search, page = 1, pageSize = PAGE_SIZE) => {
  let query = supabase
    .from("HQs")
    .select(
      `
      HQID,
      created_at,
      Code,
      Description,
      HQUser,
      HQPass,
      Active
    `,
      { count: "exact" }
    ) // ✅ Enable counting
    .order("created_at", { ascending: false }) // Optional: sort newest first
    .range((page - 1) * pageSize, page * pageSize - 1); // ✅ Pagination

  if (
    search &&
    typeof search.type === "string" &&
    typeof search.which === "string" &&
    typeof search.text === "string" &&
    search.text.trim() !== ""
  ) {
    const field = search.type;
    const value = search.text.trim();
    if (field === "HQID") {
      query = query.eq("HQID", Number(value));
    } else {
      switch (search.which.toLowerCase()) {
        case "equals":
          query = query.eq(field, value);
          break;
        case "contains":
          query = query.ilike(field, `%${value}%`);
          break;
        case "starts with":
          query = query.ilike(field, `${value}%`);
          break;
        case "ends with":
          query = query.ilike(field, `%${value}`);
          break;
        default:
          break;
      }
    }
  }

  const { data, count, error } = await query;
  return error ? { data: [], count: 0 } : { data, count };
};

const getCompanies = async (search, page = 1, pageSize = PAGE_SIZE) => {
  let query = supabase
    .from("Companies")
    .select(
      `
      CompanyID,
      created_at,
      Name,
      Description
    `,
      { count: "exact" }
    ) // ✅ Enable counting
    .order("created_at", { ascending: true }) // Optional: sort newest first
    .range((page - 1) * pageSize, page * pageSize - 1); // ✅ Pagination

  if (
    search &&
    typeof search.type === "string" &&
    typeof search.which === "string" &&
    typeof search.text === "string" &&
    search.text.trim() !== ""
  ) {
    const field = search.type;
    const value = search.text.trim();
    if (field === "CompanyID") {
      query = query.eq("CompanyID", Number(value));
    } else {
      switch (search.which.toLowerCase()) {
        case "equals":
          query = query.eq(field, value);
          break;
        case "contains":
          query = query.ilike(field, `%${value}%`);
          break;
        case "starts with":
          query = query.ilike(field, `${value}%`);
          break;
        case "ends with":
          query = query.ilike(field, `%${value}`);
          break;
        default:
          break;
      }
    }
  }

  const { data, count, error } = await query;
  return error ? { data: [], count: 0 } : { data, count };
};

const getDCTypes = async (search, page = 1, pageSize = PAGE_SIZE) => {
  let query = supabase
    .from("DriverCredentialTypes")
    .select(
      `
      DriverCredentialTypeID,
      created_at,
      Code,
      Description,
      Company:CompanyID (
        CompanyID,
        Name
      )
    `,
      { count: "exact" }
    ) // ✅ Enable counting
    .order("created_at", { ascending: false }) // Optional: sort newest first
    .range((page - 1) * pageSize, page * pageSize - 1); // ✅ Pagination

  if (
    search &&
    typeof search.type === "string" &&
    typeof search.which === "string" &&
    typeof search.text === "string" &&
    search.text.trim() !== ""
  ) {
    const field = search.type;
    const value = search.text.trim();

    if (field === "Name") {
      let CompanyFilter;

      switch (search.which.toLowerCase()) {
        case "equals":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .eq("Name", value);
          break;
        case "contains":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .ilike("Name", `%${value}%`);
          break;
        case "starts with":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .ilike("Name", `${value}%`);
          break;
        case "ends with":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .ilike("Name", `%${value}`);
          break;
        default:
          break;
      }

      const { data: Companies, error: CompanyError } = await CompanyFilter;
      if (CompanyError || !Companies?.length) return { data: [], count: 0 };

      const CompanyIDs = Companies.map((h) => h.CompanyID);
      query = query.in("CompanyID", CompanyIDs);
    } else if (field === "DriverCredentialTypeID") {
      query = query.eq("DriverCredentialTypeID", Number(value));
    } else {
      switch (search.which.toLowerCase()) {
        case "equals":
          query = query.eq(field, value);
          break;
        case "contains":
          query = query.ilike(field, `%${value}%`);
          break;
        case "starts with":
          query = query.ilike(field, `${value}%`);
          break;
        case "ends with":
          query = query.ilike(field, `%${value}`);
          break;
        default:
          break;
      }
    }
  }

  const { data, count, error } = await query;
  return error ? { data: [], count: 0 } : { data, count };
};

const getSLocations = async (search, page = 1, pageSize = PAGE_SIZE) => {
  let query = supabase
    .from("Locations")
    .select(
      `
      LocationID,
      created_at,
      Code,
      Description,
      MinutesFromAirport,
      Company:CompanyID (
        CompanyID,
        Name
      ),
      LocationType:LocationTypeID (
        LocationTypeID,
        Code
      )
    `,
      { count: "exact" }
    ) // ✅ Enable counting
    .order("created_at", { ascending: false }) // Optional: sort newest first
    .range((page - 1) * pageSize, page * pageSize - 1); // ✅ Pagination

  if (
    search &&
    typeof search.type === "string" &&
    typeof search.which === "string" &&
    typeof search.text === "string" &&
    search.text.trim() !== ""
  ) {
    const field = search.type;
    const value = search.text.trim();

    if (field === "Name") {
      let CompanyFilter;

      switch (search.which.toLowerCase()) {
        case "equals":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .eq("Name", value);
          break;
        case "contains":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .ilike("Name", `%${value}%`);
          break;
        case "starts with":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .ilike("Name", `${value}%`);
          break;
        case "ends with":
          CompanyFilter = supabase
            .from("Companies")
            .select("CompanyID")
            .ilike("Name", `%${value}`);
          break;
        default:
          break;
      }

      const { data: Companies, error: CompanyError } = await CompanyFilter;
      if (CompanyError || !Companies?.length) return { data: [], count: 0 };

      const CompanyIDs = Companies.map((h) => h.CompanyID);
      query = query.in("CompanyID", CompanyIDs);
    } else if (field === "LCode") {
      let LocationTypeFilter;

      switch (search.which.toLowerCase()) {
        case "equals":
          LocationTypeFilter = supabase
            .from("LocationTypes")
            .select("LocationTypeID")
            .eq("Code", value);
          break;
        case "contains":
          LocationTypeFilter = supabase
            .from("LocationTypes")
            .select("LocationTypeID")
            .ilike("Code", `%${value}%`);
          break;
        case "starts with":
          LocationTypeFilter = supabase
            .from("LocationTypes")
            .select("LocationTypeID")
            .ilike("Code", `${value}%`);
          break;
        case "ends with":
          LocationTypeFilter = supabase
            .from("LocationTypes")
            .select("LocationTypeID")
            .ilike("Code", `%${value}`);
          break;
        default:
          break;
      }

      const { data: LocationTypes, error: LocationTypeError } =
        await LocationTypeFilter;
      if (LocationTypeError || !LocationTypes?.length)
        return { data: [], count: 0 };

      const LocationTypeIDs = LocationTypes.map((h) => h.LocationTypeID);
      query = query.in("LocationTypeID", LocationTypeIDs);
    } else if (field === "LocationID") {
      query = query.eq("LocationID", Number(value));
    } else if (field === "MinutesFromAirport") {
      query = query.eq("MinutesFromAirport", Number(value));
    } else {
      switch (search.which.toLowerCase()) {
        case "equals":
          query = query.eq(field, value);
          break;
        case "contains":
          query = query.ilike(field, `%${value}%`);
          break;
        case "starts with":
          query = query.ilike(field, `${value}%`);
          break;
        case "ends with":
          query = query.ilike(field, `%${value}`);
          break;
        default:
          break;
      }
    }
  }

  const { data, count, error } = await query;
  return error ? { data: [], count: 0 } : { data, count };
};

const useAirlineConditions = () =>
  useQuery({
    queryKey: ["airlineConditions"],
    queryFn: getAirlineConditionsList,
    retry: 0,
  });

const useTripConditions = () =>
  useQuery({
    queryKey: ["tripConditions"],
    queryFn: getTripConditionsList,
    retry: 0,
  });

const useManagerAndLocations = () =>
  useQuery({
    queryKey: ["managerAndLocations"],
    queryFn: getManagersAndLocations,
    retry: 0,
  });

const useReportConditions = () =>
  useQuery({
    queryKey: ["reportConditions"],
    queryFn: getReportConditions,
    retry: 0,
  });

const useSearchConditions = () =>
  useQuery({
    queryKey: ["searchConditions"],
    queryFn: getSearchConditions,
    retry: 0,
  });

const useLocations = () =>
  useQuery({
    queryKey: ["locations"],
    queryFn: getLocations,
    retry: 0,
  });

const useManagers = () =>
  useQuery({
    queryKey: ["managers"],
    queryFn: getManagers,
    retry: 0,
  });

const useManagerDetail = (managerID) =>
  useQuery({
    queryKey: ["manager_detail", { managerID }],
    queryFn: () => getManagerDetail(managerID),
    retry: 0,
    enabled: !!managerID,
  });
const useScheduleDetail = (scheduleID) =>
  useQuery({
    queryKey: ["schedule_detail", { scheduleID }],
    queryFn: () => getScheduleDetail(scheduleID),
    retry: 0,
    enabled: !!scheduleID,
  });
const useDriverDetail = (driverId) =>
  useQuery({
    queryKey: ["driver_detail", { driverId }],
    queryFn: () => getDriverDetail(driverId),
    retry: 0,
    enabled: !!driverId,
  });

const useDriverNotes = (isEdit, search, page) =>
  useQuery({
    queryKey: ["driver_notes", { isEdit, search, page }],
    queryFn: () => getDriverNotes(search, page),
    retry: 0,
  });

const useHQCompanies = (isEdit, search, page) =>
  useQuery({
    queryKey: ["HQ_Companies", { isEdit, search, page }],
    queryFn: () => getHQCompanies(search, page),
    retry: 0,
  });

const useHQs = (isEdit, search, page) =>
  useQuery({
    queryKey: ["HQs", { isEdit, search, page }],
    queryFn: () => getHQs(search, page),
    retry: 0,
  });

const useCompanies = (isEdit, search, page) =>
  useQuery({
    queryKey: ["HQs", { isEdit, search, page }],
    queryFn: () => getCompanies(search, page),
    retry: 0,
  });

const useDCTypes = (isEdit, search, page) =>
  useQuery({
    queryKey: ["driver_credential_type", { isEdit, search, page }],
    queryFn: () => getDCTypes(search, page),
    retry: 0,
  });

const useSLocations = (isEdit, search, page) =>
  useQuery({
    queryKey: ["s_location", { isEdit, search, page }],
    queryFn: () => getSLocations(search, page),
    retry: 0,
  });

export {
  useAirlineConditions,
  useTripConditions,
  useManagerAndLocations,
  useReportConditions,
  useSearchConditions,
  useLocations,
  useSLocations,
  useManagers,
  useDriverDetail,
  useManagerDetail,
  useDriverNotes,
  useScheduleDetail,
  useHQs,
  useCompanies,
  useHQCompanies,
  useDCTypes,
};
